<template>
  <div class="ow-otp-validation-inputs" @paste="handlePaste">
    <input
      v-for="(_, index) in length"
      :key="index"
      :ref="el => otpInputRefs[index] = el"
      v-model="otpInputValue[index]"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      class="ow-otp-validation-input"
      :maxlength="1"
      :class="{ 'is-required': !isOtpValid }"
      @input="handleInput(index)"
      @keydown="handleKeyDown($event, index)"
      @beforeinput="validateInput"
      >
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
// import { useLoader } from "@oxia/client";

interface IOtpValidationInputs {
  length: number
  isOtpValid: boolean
}

const props = defineProps<IOtpValidationInputs>();

const emit = defineEmits(['otpCompleted']);

const otpInputRefs = ref<Element[]>([]);
const otpInputValue = ref(Array(props.length).fill(''));

const otpInputValuesJoined = computed(() => {
  return otpInputValue.value.every(value => value !== '');
});

const validateInput = (event) => {
  const input = event.data;
  if (input && !/^\d$/.test(input)) {
    event.preventDefault();
  }
};

const handleInput = (index) => {
  otpInputValue.value[index] = otpInputValue.value[index].replace(/\D/g, '');

  if (otpInputValue.value[index].length === 1 && index < props.length - 1) {
    otpInputRefs.value[index + 1].focus();
  }
};

const handleKeyDown = (event, index) => {
  if (event.key === 'Backspace') {
    if (otpInputValue.value[index] === '') {
      if (index > 0) {
        otpInputValue.value[index - 1] = '';
        otpInputRefs.value[index - 1].focus();
      }
    }
    else {
      otpInputValue.value[index] = '';
    }
  }
};

const handlePaste = (event) => {
  const pastedText = event.clipboardData.getData('text/plain');
  if (pastedText.toString().length === 6) {
    otpInputValue.value.forEach((value, index) => {
      otpInputValue.value[index] = pastedText[index];
    });
  }
};

const focusFirstInput = () => {
  otpInputRefs?.value[0]?.focus();
};

const resetFields = () => {
  otpInputValue.value = Array(props.length).fill('');
  setTimeout(() => {
    focusFirstInput();
  }, 300);
};

defineExpose({
  resetFields,
  focusFirstInput
});

watch(() => otpInputValuesJoined.value, (complete) => {
  if (complete) {
    const otpString = otpInputValue.value.join('');
    emit('otpCompleted', otpString);

    nextTick(() => {
      if (otpInputRefs.value && otpInputRefs.value[props.length - 1]) otpInputRefs.value[props.length - 1].blur();
    });
  }
});
</script>

<style lang="scss">
@use 'sass:color';
.ow-otp-validation-inputs {
  display: flex;
  gap: 10px;
  justify-content: center;
  height: 60px;
  & .ow-otp-validation-input {
    width: 40px;
    height: 40px;
    text-align: center;
    font-size: 18px;
    border: 1px solid #fff;
    border-radius: 4px;
    background-color: #fafafa;
    box-shadow: inset 2px 2px 4px rgba(0, 0, 0, 0.15);
    color: #949393;
    font-weight: 700;

    &:focus {
      outline: none;
      border: 1.5px solid color.adjust(#0893fd, $lightness: -10%);
    }

    &.is-required {
      position: relative;
      box-shadow: inset 2px 2px 4px rgba(221, 0, 0, 0.427);
    }
  }
  @media only screen and (max-width:620px) {
    gap: 5px;
    & .ow-otp-validation-input {
      width: 30px;
      height: 30px;
    }
  }
}
</style>
